import { useEffect, useState, useCallback } from 'react';
import {
  ArchiosExpressionProfile,
  ExpressionBuilderView,
  ExpressionContextMenuComponent,
  ExpressionTokenComponent,
  GroupToken,
  StandardExpressionBuilderView,
  Syntax,
} from '@trlogic/ui-pack';
import { generateUUIDV4 } from '../../../../util/Generator';
import DataType from '../../../../constant/DataType';
import { ExpressionUtil } from '../../../../util/ExpressionUtil';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { setCraApiVariable } from '../../../../redux/actions/CraApiVariableActions';
import { CraApiVariableSchema } from '../../../../domain/cra/CraDomains';
import VariableType from '../../../../constant/VariableType';
import GatewayContainer from '../../../../config/GatewayContainer';
import GatewayNames from '../../../../constant/GatewayNames';
import StructClient from '../../../../client/struct/StructClient';
import { EntryProperties, ObjectVariableProperties } from '../../../../domain/datatype/VariablePropertiesDomains';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import ExpressionBridge from '../../../../util/ExpressionBridge';

interface MappingProps {
  onChange: (expression: Syntax) => void;
  variableName: string;
}

function MappingBuilderComponent(props: MappingProps) {
  const { onChange, variableName } = props;
  const { craApiVariables } = useSelector((state: RootState) => state.craApiVariable);
  const dispatch = useDispatch();
  const currentVariable = craApiVariables[variableName];
  const structClient = GatewayContainer.getInstance().get<StructClient>(GatewayNames.STRUCT_CLIENT);

  const safeCopy = (syntax: Syntax): Syntax => {
    return {
      id: syntax.id,
      value: syntax.value,
      children: syntax.children?.map((child: Syntax) => safeCopy(child)) || [],
    };
  };

  const [expressionView, setExpressionView] = useState<ExpressionBuilderView | undefined>(undefined);
  const [structVariables, setStructVariables] = useState<EntryProperties[]>([]);
  const [expression, setExpression] = useState<Syntax>(() => {
    if (currentVariable?.mapping) {
      return safeCopy(currentVariable.mapping);
    }
    return {
      id: generateUUIDV4(),
      value: {
        type: 'TokenGroup',
        properties: {
          dataType: DataType.BOOLEAN,
        },
      } as GroupToken,
      children: [],
    };
  });

  useEffect(() => {
    getStructData();
  }, [currentVariable?.properties?.name]);

  useEffect(() => {
    expressionView?.initialize();
  }, [expressionView, expression]);

  useEffect(() => {
    if (currentVariable?.mapping) {
      const currentMappingId = currentVariable.mapping.id;
      const expressionId = expression.id;

      if (currentMappingId !== expressionId) {
        setExpression(safeCopy(currentVariable.mapping));
      }
    }
  }, [currentVariable?.mapping]);

  const prepareVariables = useCallback(async (): Promise<any> => {
    if (!currentVariable?.properties) return;

    if (currentVariable?.properties?.dataType !== 'Structure') {
      const simpleVariable = {
        type: VariableType.API,
        variable: {
          type: 'Common Variable',
          variableType: VariableType.API,
          properties: currentVariable?.properties || { dataType: DataType.STRING },
          name: 'response',
        },
      };

      return {
        [VariableType.API]: [simpleVariable],
      };
    }

    const structVariable = {
      type: VariableType.API,
      variable: {
        type: 'Common Variable',
        variableType: VariableType.API,
        // Şu anda statik 'response.xxx' olarak kullanılıyor, ileride gerçek struct ismi gerekirse:
        // name: currentVariable.properties.name || 'testApi'
        name: 'response',
        properties: currentVariable.properties,
        args: {},
      },
    };

    return {
      [VariableType.API]: [structVariable],
    };
  }, [currentVariable?.properties, structVariables]);

  const getStructData = async () => {
    if (!currentVariable.properties.name) return;
    const structs = await structClient.getById(currentVariable.properties.name);
    const objectProperties = structs.properties as ObjectVariableProperties;
    setStructVariables(objectProperties.variables || []);
  };

  const buildData = useCallback(async () => {
    const variables = await prepareVariables();

    const expressionBridge: ExpressionBridge = ServiceContainer.getInstance().get(ServiceNames.EXPRESSION_BRIDGE);
    await expressionBridge.start(expression, variables);

    const expressionView: ExpressionBuilderView = new StandardExpressionBuilderView({
      el: '#expression_builder',
      model: expression,
      profile: new ArchiosExpressionProfile(),
      returnType: { dataType: DataType.NUMBER },
      onCtrlToken: ExpressionUtil.onNavigateToken,
    });
    expressionView.getContext().setVariables(variables);
    expressionView.onChange(onChangeSyntax as VoidFunction);
    setExpressionView(expressionView);
  }, [expression, prepareVariables]);

  useEffect(() => {
    buildData();
  }, [buildData]);

  const onChangeSyntax = (updatedExpression: Syntax): void => {
    setExpression(updatedExpression);
    onChangeSyntaxBuilder(updatedExpression);
  };

  const onChangeSyntaxBuilder = (syntax: Syntax) => {
    if (currentVariable) {
      const updatedVariable: CraApiVariableSchema = {
        ...currentVariable,
        mapping: syntax,
      };
      dispatch(setCraApiVariable(variableName, updatedVariable));
    }
    onChange(syntax);
  };

  return (
    <div className='bg-main-gray border border-f-black-7 p-3 rounded w-full'>
      <div className='bg-white p-4 rounded' id='expression_builder'>
        {expression && expressionView && (
          <>
            {createPortal(<ExpressionContextMenuComponent context={expressionView.getContext()} close={() => {}} />, document.body)}
            <ExpressionTokenComponent syntax={expressionView.getModel()} view={expressionView} />
          </>
        )}
      </div>
    </div>
  );
}

export default MappingBuilderComponent;
